import React from 'react';
import { FormControl, InputLabel, MenuItem, Box, useTheme } from '@mui/material';
import { StyledSelect, StyledSelectMinimal, menuItemSx, selectFormControlSx } from './InputStyles';

/**
 * SelectInput - компонент для выбора из списка
 *
 * @param {string} label - Текст label
 * @param {any} value - Выбранное значение (key)
 * @param {function} onChange - Callback при изменении (получает value)
 * @param {Array<{key: any, value: string}>} options - Массив опций
 * @param {boolean} disabled - Заблокировано ли поле
 * @param {boolean} fullWidth - На всю ширину (по умолчанию true)
 */
export default function SelectInput({
    label,
    value,
    onChange,
    options = [],
    disabled = false,
    fullWidth = true,
    ...rest
}) {
    const theme = useTheme();

    const handleChange = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onChange) {
            onChange(event.target.value);
        }
    };

    const renderValue = () => {
        const selected = options.find((item) => item.key === value);
        return selected ? selected.value : '';
    };

    const labelSx = {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 600,
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
        },
    };

    return (
        <FormControl fullWidth={fullWidth} disabled={disabled} sx={selectFormControlSx(theme)}>
            {label && <InputLabel sx={labelSx}>{label}</InputLabel>}
            <StyledSelect
                value={value ?? ''}
                onChange={handleChange}
                label={label}
                renderValue={renderValue}
                {...rest}
            >
                {options.map((item, index) => (
                    <MenuItem value={item.key} key={index} sx={menuItemSx}>
                        {item.value}
                    </MenuItem>
                ))}
            </StyledSelect>
        </FormControl>
    );
}

/**
 * SelectInputMinimal - компактный Select без бордера (для таблиц)
 *
 * @param {any} value - Выбранное значение (key)
 * @param {function} onChange - Callback при изменении (получает value, name)
 * @param {Array<{key: any, value: string}>} options - Массив опций
 * @param {boolean} disabled - Заблокировано ли поле
 * @param {string} name - Имя поля (передаётся в onChange вторым аргументом)
 */
export function SelectInputMinimal({
    value,
    onChange,
    options = [],
    disabled = false,
    name,
    ...rest
}) {
    const handleChange = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onChange) {
            onChange(event.target.value, name);
        }
    };

    return (
        <Box>
            <FormControl fullWidth disabled={disabled}>
                <StyledSelectMinimal
                    value={value ?? ''}
                    onChange={handleChange}
                    autoWidth
                    {...rest}
                >
                    {options.map((item, index) => (
                        <MenuItem value={item.key} key={index} sx={menuItemSx}>
                            {item.value}
                        </MenuItem>
                    ))}
                </StyledSelectMinimal>
            </FormControl>
        </Box>
    );
}

/**
 * MultiSelectInput - компонент для множественного выбора
 *
 * @param {string} label - Текст label
 * @param {Array} value - Массив выбранных значений (keys)
 * @param {function} onChange - Callback при изменении (получает массив values)
 * @param {Array<{key: any, value: string}>} options - Массив опций
 * @param {boolean} disabled - Заблокировано ли поле
 */
export function MultiSelectInput({
    label,
    value = [],
    onChange,
    options = [],
    disabled = false,
    ...rest
}) {
    const theme = useTheme();

    const handleChange = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onChange) {
            onChange(event.target.value);
        }
    };

    const renderValue = (selected) => {
        return options
            .filter((item) => selected.includes(item.key))
            .map((item) => item.value)
            .join(', ');
    };

    const selectedStyle = {
        fontWeight: 600,
        backgroundColor: 'rgba(93, 135, 255, 0.1)',
    };

    const labelSx = {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 600,
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
        },
    };

    return (
        <FormControl fullWidth disabled={disabled} sx={selectFormControlSx(theme)}>
            {label && <InputLabel sx={labelSx}>{label}</InputLabel>}
            <StyledSelect
                multiple
                value={value}
                onChange={handleChange}
                label={label}
                renderValue={renderValue}
                {...rest}
            >
                {options.map((item, index) => (
                    <MenuItem
                        value={item.key}
                        key={index}
                        sx={{
                            ...menuItemSx,
                            ...(value.includes(item.key) ? selectedStyle : {}),
                        }}
                    >
                        {item.value}
                    </MenuItem>
                ))}
            </StyledSelect>
        </FormControl>
    );
}
